import {
  Typography,
  Container,
  Link,
  Box,
  LinearProgress,
} from '@mui/material';
import { FC, useState, Dispatch, SetStateAction } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { FormProvider, useForm } from '@carcareers/contexts';
import {
  AccountInformationStep,
  VerifyOtpStep,
  ResumeUploadStep,
  ExperienceStep,
  EducationStep,
} from '@carcareers/components';

import CreateJobseekerStepNav from './CreateJobseekerStepNav';

export const CreateJobseeker: FC = () => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    'Account Information',
    'Verify Your Email',
    'Upload Resume',
    'Experience',
    'Education',
  ];

  return (
    <Container maxWidth="md" sx={{ pt: 8 }}>
      <Typography component="h2" variant="body1" align="center" my={3}>
        Looking for talented employees? Sign up as an employer{' '}
        <Link
          component={RouterLink}
          sx={{
            textDecoration: 'none',
            color: 'primary.main',
            fontWeight: 'bold',
            '&:active': { color: 'primary.dark' },
            '&:hover': { textDecoration: 'underline' },
          }}
          to="/auth/signup/employer"
        >
          here
        </Link>
        .
      </Typography>
      <Typography component="h1" variant="h3" align="center" my={3}>
        Create Your Jobseeker Account
      </Typography>
      <FormProvider>
        <Stepper
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          steps={steps}
        />
      </FormProvider>
    </Container>
  );
};

interface StepperProps {
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  steps: string[];
}

const Stepper: FC<StepperProps> = ({ 
  activeStep, 
  setActiveStep, 
  steps 
}) => {
  const { errors } = useForm();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', p: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Step {activeStep + 1} of {steps.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(((activeStep + 1) / steps.length) * 100)}% Complete
          </Typography>
        </Box>
        <LinearProgress
          color={errors.length > 0 ? 'error' : 'primary'}
          variant="determinate"
          value={((activeStep + 1) / steps.length) * 100}
          sx={{
            height: 10,
            borderRadius: 5,
            '& .MuiLinearProgress-bar': {
              borderRadius: 5,
              backgroundColor: errors.length > 0 ? 'error.main' : 'primary.main',
            },
          }}
        />
      </Box>

      {activeStep === 0 && <AccountInformationStep />}
      {activeStep === 1 && <VerifyOtpStep />}
      {activeStep === 2 && <ResumeUploadStep />}
      {activeStep === 3 && <ExperienceStep />}
      {activeStep === 4 && <EducationStep />}

      <CreateJobseekerStepNav activeStep={activeStep} setActiveStep={setActiveStep} />
    </Box>
  );
}; 

export default CreateJobseeker;
